// Search page look: colours and spacing live here so a later visual polish
// only touches this file. Class names are written out in full so Tailwind
// can see them.

export const SEARCH = {
  page: "flex h-full min-h-0 bg-violet-50/[0.15]",
  setupColumn: "w-[28.75rem] shrink-0 flex flex-col min-h-0 bg-white border-r border-slate-200",
  setupScroll: "flex-1 min-h-0 overflow-y-auto px-5 py-3 space-y-3",
  section: "",
  sectionHeader: "flex items-baseline justify-between mb-1",
  sectionTitle: "text-[0.6875rem] font-semibold text-slate-500 uppercase tracking-wider",
  sectionHint: "text-[0.6875rem] text-slate-400",
  input: "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-violet-400 bg-white",
  inputSmall: "w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-violet-400 bg-white",
  tagRow: "flex flex-wrap gap-1.5 mt-2",
  keywordTag: "inline-flex items-center gap-1.5 bg-slate-100 text-slate-700 border border-slate-200 text-xs pl-2.5 pr-1.5 py-1 rounded-full font-medium",
  // Highlights (postcode tags, chosen options, main buttons) are pale brand
  // purple with dark purple text, as on the reports - never black.
  postcodeTag: "inline-flex items-center gap-1 bg-violet-100 text-violet-800 border border-violet-200 text-xs pl-2.5 pr-1.5 py-1 rounded-full font-medium",
  tagRemove: "text-slate-400 hover:text-slate-900 leading-none px-0.5",
  postcodeTagRemove: "text-violet-400 hover:text-violet-900 leading-none px-0.5",
  // The business postcode in Business locations: ringed like its map pin, no ×.
  postcodeTagBusiness: "inline-flex items-center gap-1.5 bg-violet-100 text-violet-800 border border-violet-200 text-xs px-2.5 py-1 rounded-full font-medium ring-2 ring-offset-1 ring-violet-300",
  postcodeTagNote: "text-violet-500 font-normal",
  legend: "flex items-center gap-3 mt-1.5 text-[0.6875rem] text-slate-400",
  // Locations: small labels inside the section, the radius slider, notes.
  subLabel: "text-xs font-medium text-slate-600 mb-1",
  slider: "w-full accent-violet-500 cursor-pointer",
  sliderTicks: "flex justify-between text-[0.6875rem] text-slate-400 -mt-0.5",
  note: "text-[0.6875rem] text-slate-500 mt-1",
  nicheBox: "border border-slate-200 rounded-lg px-3 py-2 bg-violet-50",
  nicheText: "text-sm text-slate-700",
  nicheStrong: "font-semibold text-slate-900",
  nicheMuted: "text-sm text-slate-400",
  linkButton: "text-xs font-semibold text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline",
  dropdown: "absolute z-[1000] left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-sm max-h-56 overflow-y-auto",
  dropdownItem: "w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-violet-100",
  dropdownItemActive: "w-full text-left px-3 py-1.5 text-sm text-slate-900 bg-violet-100",
  dropdownHint: "text-xs text-slate-400 ml-2",
  dropdownNote: "px-3 py-1.5 text-xs text-slate-500",
  suggestRow: "flex items-center justify-between gap-3 px-3 py-2 text-sm text-slate-600 border-t border-slate-100 first:border-t-0",
  suggestButton: "shrink-0 bg-violet-100 text-violet-800 border border-violet-200 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-violet-200 transition-colors disabled:opacity-40",
  dropdownError: "px-3 py-1.5 text-xs text-red-600",
  error: "text-xs text-red-600 mt-1",
  segmentGroup: "flex gap-1",
  segment: "px-2.5 py-1.5 rounded-md text-xs font-semibold border bg-white text-slate-600 border-slate-200 hover:border-violet-300",
  segmentActive: "px-2.5 py-1.5 rounded-md text-xs font-semibold border bg-violet-100 text-violet-800 border-violet-300",
  summary: "shrink-0 border-t border-slate-200 px-5 py-3 bg-white space-y-1.5",
  summaryTop: "flex items-baseline justify-between gap-3 text-sm",
  summaryName: "font-semibold text-slate-900 truncate",
  summaryCount: "font-semibold text-slate-900 shrink-0",
  summaryLine: "text-xs text-slate-500",
  warning: "text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1",
  runButton: "w-full bg-violet-100 text-violet-800 border border-violet-200 font-semibold py-2.5 rounded-lg text-sm hover:bg-violet-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
  mapColumn: "flex-1 min-w-0 flex flex-col",
  mapHeader: "shrink-0 flex items-center justify-between gap-4 px-4 py-2 bg-white border-b border-slate-200 text-xs",
  mapTitle: "font-semibold text-slate-800",
  mapMuted: "text-slate-400",
  mapArea: "flex-1 min-h-0",
};

// Search / Upload CSV tabs at the top of the search page.
export const SEARCH_TABS = {
  row: "flex gap-1 mb-1",
  tab: "px-3 py-1.5 rounded-md text-xs font-semibold text-slate-500 hover:text-slate-900 hover:bg-slate-50",
  tabActive: "px-3 py-1.5 rounded-md text-xs font-semibold bg-violet-100 text-violet-800",
};

// The Upload CSV tab: full width, a preview table, totals at the bottom.
export const CSV = {
  page: "flex flex-col h-full min-h-0 bg-white",
  top: "shrink-0 px-5 pt-3 pb-3 border-b border-slate-200 space-y-2",
  intro: "text-xs text-slate-500 max-w-3xl",
  code: "font-mono text-[0.6875rem] text-slate-700 bg-slate-100 rounded px-1",
  actions: "flex flex-wrap items-center gap-2",
  button: "bg-violet-100 text-violet-800 border border-violet-200 text-xs font-semibold px-3 py-1.5 rounded-md hover:bg-violet-200 transition-colors cursor-pointer",
  buttonQuiet: "bg-white text-slate-600 border border-slate-200 text-xs font-semibold px-3 py-1.5 rounded-md hover:border-violet-300 transition-colors",
  fileName: "text-xs text-slate-600",
  scroll: "flex-1 min-h-0 overflow-auto",
  empty: "px-5 py-10 text-sm text-slate-400",
  fileError: "mx-5 mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 max-w-3xl",
  table: "w-full text-xs text-slate-700 border-collapse",
  th: "sticky top-0 bg-slate-50 text-left font-semibold text-slate-500 uppercase tracking-wider text-[0.6875rem] px-3 py-2 border-b border-slate-200",
  thNumber: "sticky top-0 bg-slate-50 text-right font-semibold text-slate-500 uppercase tracking-wider text-[0.6875rem] px-3 py-2 border-b border-slate-200",
  td: "align-top px-3 py-2 border-b border-slate-100",
  tdNumber: "align-top px-3 py-2 border-b border-slate-100 text-right font-semibold text-slate-900",
  rowBlocked: "bg-red-50/40",
  strong: "font-semibold text-slate-900",
  muted: "text-slate-400",
  tag: "inline-block bg-slate-100 text-slate-600 border border-slate-200 rounded-full px-2 py-0.5 mr-1 mt-1 text-[0.6875rem]",
  code2: "inline-block bg-violet-50 text-violet-800 border border-violet-100 rounded px-1 mr-1 mt-0.5",
  flag: "mt-1 text-amber-700",
  errorList: "space-y-0.5 text-red-700",
  ready: "text-slate-700 font-semibold",
  footer: "shrink-0 border-t border-slate-200 px-5 py-3 bg-white flex flex-wrap items-center justify-between gap-3",
  runButton: "bg-violet-100 text-violet-800 border border-violet-200 font-semibold px-5 py-2 rounded-lg text-sm hover:bg-violet-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed",
  totals: "text-sm text-slate-700",
};

// Data dots on keyword tags: neutral slate; the fill shows how much data we hold.
export const DATA_DOT = {
  full: "w-1.5 h-1.5 rounded-full bg-slate-600",
  half: "w-1.5 h-1.5 rounded-full bg-gradient-to-r from-slate-600 from-50% to-transparent to-50% border border-slate-600",
  empty: "w-1.5 h-1.5 rounded-full border border-slate-400",
  placeholder: "w-1.5 h-1.5 rounded-full border border-dashed border-slate-300",
};

// Map pins (Leaflet needs real colour values, not class names): areas in the
// soft purple (violet-400/500), your business in the brand purple (violet-600).
export const MAP_PIN = {
  area: { radius: 6, color: "#8b5cf6", weight: 1.5, fillColor: "#a78bfa", fillOpacity: 0.9 },
  businessRing: { radius: 11, color: "#7c3aed", weight: 2, fillOpacity: 0 },
  businessDot: { radius: 6, color: "#7c3aed", weight: 1, fillColor: "#7c3aed", fillOpacity: 1 },
  // The radius round each business location.
  radiusCircle: { color: "#8b5cf6", weight: 1.5, dashArray: "5 5", fillColor: "#a78bfa", fillOpacity: 0.06 },
  label: "!bg-white !border-slate-200 !text-slate-700 !text-[0.6875rem] !font-semibold !px-1.5 !py-0.5 !shadow-sm",
};

// Legend swatches in the map header (match MAP_PIN).
export const MAP_LEGEND = {
  area: "inline-block w-2.5 h-2.5 rounded-full bg-violet-400 border border-violet-500",
  business: "inline-block w-2.5 h-2.5 rounded-full bg-violet-600 ring-2 ring-offset-1 ring-violet-600",
};
